import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "You can go from zero to a live, grounded AI Twin in about a minute. Here's the whole path.",
  },
  { type: "h2", text: "1. Create your account" },
  {
    type: "p",
    text: (
      <>
        Sign up at{" "}
        <Link to="/auth" className="underline hover:text-foreground">
          /auth
        </Link>{" "}
        with email or Google. If email confirmation is on, confirm via the link, then sign in —
        you'll land in onboarding.
      </>
    ),
  },
  { type: "h2", text: "2. Build the twin from your website" },
  {
    type: "p",
    text: "In onboarding, paste your website URL and a short description. Reality Twin crawls the site, splits it into sentence-aware chunks, embeds them, and builds a citable knowledge graph — usually in under a minute for a typical site. Your first twin is live immediately.",
  },
  {
    type: "callout",
    title: "No website yet?",
    text: "You can still create a twin from a description and upload documents instead — the knowledge base doesn't require a crawl.",
  },
  { type: "h2", text: "3. Teach it more (optional)" },
  {
    type: "p",
    text: (
      <>
        Add depth on the{" "}
        <Link to="/docs/knowledge-base" className="underline">
          Knowledge
        </Link>{" "}
        page: upload PDFs, DOCX, Markdown or text, or trigger a manual re-crawl. The twin re-crawls
        your site weekly on its own.
      </>
    ),
  },
  { type: "h2", text: "4. Tune the personality" },
  {
    type: "p",
    text: (
      <>
        On the{" "}
        <Link to="/docs/twin-personality" className="underline">
          Twin
        </Link>{" "}
        page, set voice, tone and how it should qualify and hand off leads — or let AI draft the
        personality from your indexed knowledge.
      </>
    ),
  },
  { type: "h2", text: "5. Deploy it" },
  {
    type: "p",
    text: "Add one script tag to your site to show the chat widget, publish your public marketplace profile, or connect an AI assistant over MCP.",
  },
  {
    type: "code",
    lang: "html",
    code: `<script src="https://realitytwin.io/api/public/embed/YOUR_TWIN_ID" async></script>`,
  },
  { type: "h2", text: "6. Connect leads to your CRM (optional)" },
  {
    type: "p",
    text: (
      <>
        In Settings → Integrations, paste a webhook URL and Reality Twin will POST every captured
        lead to it, HMAC-signed. See{" "}
        <Link to="/docs/leads-and-crm" className="underline">
          Leads & CRM
        </Link>
        .
      </>
    ),
  },
  {
    type: "callout",
    title: "That's it",
    text: "Your twin now answers visitors 24/7, captures leads, and improves as it learns from real conversations.",
  },
];

export const Route = createFileRoute("/docs/quickstart")({
  head: () =>
    buildSeo({
      path: "/docs/quickstart",
      title: "Quickstart — Reality Twin Docs",
      description:
        "Create your first AI Twin in about 60 seconds: sign up, crawl your site, tune, deploy.",
    }),
  component: () => (
    <DocsPageShell
      slug="quickstart"
      title="Quickstart"
      description="From zero to a live, grounded AI Twin in about a minute."
      blocks={blocks}
    />
  ),
});
